<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import {
  DialogRoot,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogDescription,
  AlertDialogRoot,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "reka-ui";
import { useDeviceStore } from "@/stores/deviceStore";
import { startLogout } from "@/lib/auth";
import { MobilePhone, Trash, Retry } from "@/components/icons";
import LoadingIndicator from "@/components/general/LoadingIndicator.vue";
import LoadingButton from "@/components/general/LoadingButton.vue";

const { t } = useI18n();
const deviceStore = useDeviceStore();
const confirmDeleteDevice = ref<{ id: number; name: string } | null>(null);
const continueLoading = ref(false);

watch(
  () => deviceStore.showDeviceLimitWall,
  (show) => {
    if (show) {
      deviceStore.fetchDevices();
    }
  },
  { immediate: true }
);

function getDeviceDisplayName(device: { brand: string | null; model: string | null; os_name: string }) {
  if (device.brand && device.model) return `${device.brand} ${device.model}`;
  if (device.brand) return device.brand;
  if (device.model) return device.model;
  return device.os_name;
}

function formatDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  return format(new Date(dateStr), "dd-MM-yyyy", { locale: nl });
}

function handleDeleteConfirmed(id: number) {
  deviceStore.removeDevice(id);
}

async function handleContinue() {
  continueLoading.value = true;
  await deviceStore.continueAfterDelete();
  continueLoading.value = false;
}

function handleLogout() {
  startLogout();
}

function preventDismiss(e: Event) {
  e.preventDefault();
}
</script>

<template>
  <DialogRoot :open="deviceStore.showDeviceLimitWall">
    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 bg-background-overlay/40 z-50"
      />
      <DialogContent
        class="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] bg-background-primary rounded-xl border border-border-primary shadow-card z-50 w-xl max-w-[calc(100vw-1.25rem)] max-h-[calc(100dvh-2.5rem)] overflow-y-auto"
        @interactOutside="preventDismiss"
        @escapeKeyDown="preventDismiss"
      >
        <div class="p-6 w-full">
          <div class="flex flex-col items-center text-center mb-6">
            <MobilePhone :size="48" class="text-icon-brand mb-4" />
            <DialogTitle class="text-xl font-bold mb-2">
              {{ t("device_limit.title") }}
            </DialogTitle>
            <DialogDescription class="text-text-secondary">
              {{ t("device_limit.description") }}
            </DialogDescription>
          </div>

          <!-- Loading state -->
          <div v-if="deviceStore.isLoadingDevices" class="flex justify-center py-8">
            <LoadingIndicator />
          </div>

          <!-- Error state -->
          <div v-else-if="deviceStore.loadDevicesError" class="text-center py-8">
            <p class="font-bold">{{ t("common.error.title") }}</p>
            <p class="text-text-secondary">{{ t("common.error.description") }}</p>
            <button
              class="btn-white btn--border mt-3"
              @click="deviceStore.fetchDevices()"
            >
              {{ t("common.error.retry") }}
            </button>
          </div>

          <!-- Device list -->
          <div v-else class="divide-y divide-border-primary rounded-lg border border-border-primary">
            <div
              v-for="device in deviceStore.devices"
              :key="device.id"
              class="flex items-center gap-3 px-4 py-3"
            >
              <MobilePhone :size="20" class="text-icon-secondary shrink-0" />

              <div class="flex-1 min-w-0">
                <p class="font-medium truncate">
                  {{ getDeviceDisplayName(device) }}
                </p>
                <p
                  v-if="device.os_id === deviceStore.currentOsId"
                  class="text-sm text-text-brand font-medium"
                >
                  {{ t("device_limit.this_device") }}
                </p>
                <p v-if="device.last_used_at" class="text-sm text-text-secondary">
                  {{ t("device_limit.last_used") }}
                  {{ formatDate(device.last_used_at) }}
                </p>
              </div>

              <!-- Current device: logout button -->
              <button
                v-if="device.os_id === deviceStore.currentOsId"
                class="btn-secondary text-sm px-3 py-1 shrink-0"
                @click="handleLogout"
              >
                {{ t("common.logout") }}
              </button>

              <!-- Other device: delete states -->
              <template v-else>
                <!-- Loading -->
                <div
                  v-if="deviceStore.deleteStates[device.id] === 'loading'"
                  class="w-10 h-10 flex items-center justify-center shrink-0"
                >
                  <LoadingIndicator size="sm" />
                </div>
                <!-- Error: retry -->
                <button
                  v-else-if="deviceStore.deleteStates[device.id] === 'error'"
                  class="w-10 h-10 flex items-center justify-center text-icon-secondary hover:text-text-primary shrink-0"
                  @click="handleDeleteConfirmed(device.id)"
                >
                  <Retry :size="20" />
                </button>
                <!-- Idle: trash -->
                <button
                  v-else
                  class="w-10 h-10 flex items-center justify-center text-icon-secondary hover:text-text-primary shrink-0"
                  @click="confirmDeleteDevice = { id: device.id, name: getDeviceDisplayName(device) }"
                >
                  <Trash :size="20" />
                </button>
              </template>
            </div>
          </div>

          <!-- Continue button -->
          <LoadingButton
            v-if="deviceStore.hasDeletedDevice"
            class="w-full mt-4"
            :loading="continueLoading"
            @click="handleContinue"
          >
            {{ t("device_limit.continue") }}
          </LoadingButton>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>

  <!-- Delete confirmation dialog -->
  <AlertDialogRoot :open="!!confirmDeleteDevice">
    <AlertDialogPortal>
      <AlertDialogOverlay class="fixed inset-0 bg-background-overlay/40 z-[60]" />
      <AlertDialogContent
        class="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] bg-background-primary rounded-xl border border-border-primary shadow-card z-[60] w-md max-w-[calc(100vw-1.25rem)] p-4"
      >
        <AlertDialogTitle class="text-lg font-bold mb-2">
          {{ t("device_limit.confirm_delete_title") }}
        </AlertDialogTitle>
        <AlertDialogDescription class="text-text-secondary mb-4">
          {{ t("device_limit.confirm_delete_description", { name: confirmDeleteDevice?.name }) }}
        </AlertDialogDescription>
        <div class="grid grid-cols-2 gap-3">
          <AlertDialogCancel
            class="btn-secondary"
            @click="confirmDeleteDevice = null"
          >
            {{ t("common.cancel") }}
          </AlertDialogCancel>
          <AlertDialogAction
            class="btn-primary"
            @click="() => { if (confirmDeleteDevice) { handleDeleteConfirmed(confirmDeleteDevice.id); confirmDeleteDevice = null; } }"
          >
            {{ t("device_limit.delete") }}
          </AlertDialogAction>
        </div>
      </AlertDialogContent>
    </AlertDialogPortal>
  </AlertDialogRoot>
</template>
